import { NOT_IN } from '../constants/FilterOperatorTypes';
import useCrmObjectTypeListingFilters from 'framework-listing-lib/hooks/useCrmObjectTypeListingFilters';
import useCrmSearchQuery from 'framework-listing-lib/hooks/useCrmSearchQuery';
import { useFilterQuery } from 'framework-listing-lib/hooks/useListingLibFilters';
import { useRemoveQuickFilter } from 'framework-listing-lib/hooks/useQuickFilters';
import { useAuthInfoProvider } from 'content-management-data-lib';
import I18n from 'I18n';
import FormattedList from 'I18n/components/FormattedList';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useCallback, useMemo } from 'react';
import { Box } from 'foundations-components/transitional/components/Box';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Tag } from 'foundations-components/transitional/components/Tag';
import { SELF_USER_IDENTIFIER } from '../constants/filters';
import { getIsPropertyVisibleAsFilter } from '../utils/filterPropertyUtils';
import { isEqualOperator, isHasPropertyOperator, isInOrNotInOperator, isNotEqualOperator, isNotHasPropertyOperator, isRangeOperator } from '../utils/typeCheckUtils';
import { FilterTagDateLabel, FilterTagDescriptorLabel, FilterTagEnumLabel } from './FilterTagLabels';
import useSearchTokens from '../hooks/useSearchTokens';
import useContentPlatformPropertiesConfiguration from '../hooks/useContentPlatformPropertiesConfiguration';
import { getPropertyOptions } from '../utils/propertyOptionsUtils';
import useContentPlatformListingConfiguration from '../hooks/useContentPlatformListingConfiguration';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import { getPropertyLabel } from '../utils/propertyUtils';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const SearchTag = () => {
  const searchQuery = useFilterQuery();
  if (!searchQuery) {
    return null;
  }
  return /*#__PURE__*/_jsxs(Tag, {
    inline: true,
    children: [/*#__PURE__*/_jsx(Text, {
      as: "span",
      marginInlineEnd: "100",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "content-management-listing-lib.filterTagsBar.searchResult"
      })
    }), searchQuery]
  });
};
const getSelectedFilterTagText = (selectedValue, options) => {
  if (selectedValue === SELF_USER_IDENTIFIER) {
    return I18n.text(`content-management-listing-lib.filterTagsBar.${SELF_USER_IDENTIFIER}`);
  } else if (options && options.length > 0) {
    const selectedOption = options.find(option => option.value === selectedValue);
    if (selectedOption) {
      return selectedOption.label;
    }
  }
  return selectedValue;
};
const FilterLabel = ({
  value,
  options
}) => {
  return /*#__PURE__*/_jsx("span", {
    children: getSelectedFilterTagText(value, options)
  });
};
const FilterTags = ({
  availableFilters
}) => {
  const removeQuickFilter = useRemoveQuickFilter();
  const {
    onRemovePropertyFilter
  } = useCrmObjectTypeListingFilters();
  const {
    propertyDefinitionMap,
    filterTagsConfig
  } = useContentPlatformPropertiesConfiguration();
  const handleRemovePropertyFilter = useCallback(propertyName => {
    removeQuickFilter(propertyName);
    onRemovePropertyFilter(propertyName);
  }, [onRemovePropertyFilter, removeQuickFilter]);
  return /*#__PURE__*/_jsx(_Fragment, {
    children: availableFilters.map((filter, index) => {
      let filterLabel;
      const options = getPropertyOptions(propertyDefinitionMap, filter.property);
      const propertyLabel = getPropertyLabel(propertyDefinitionMap, filter.property);
      if (isHasPropertyOperator(filter)) {
        const key = `content-management-listing-lib.filterTagsBar.options.hasPropertyOption.${filter.property}`;
        const hasSpecificLabelForProperty = isValidI18nKey(key);
        filterLabel = /*#__PURE__*/_jsx(FormattedMessage, {
          message: hasSpecificLabelForProperty ? key : `content-management-listing-lib.filterTagsBar.options.hasPropertyOption.default`,
          options: {
            propertyLabel
          }
        }, index);
      } else if (isNotHasPropertyOperator(filter)) {
        const key = `content-management-listing-lib.filterTagsBar.options.noneOption.${filter.property}`;
        const hasSpecificLabelForProperty = isValidI18nKey(key);
        filterLabel = /*#__PURE__*/_jsx(FormattedMessage, {
          message: hasSpecificLabelForProperty ? key : `content-management-listing-lib.filterTagsBar.options.noneOption.default`,
          options: {
            propertyLabel
          }
        }, index);
      } else if (isEqualOperator(filter)) {
        filterLabel = /*#__PURE__*/_jsx(FilterLabel, {
          value: filter.value,
          options: options
        }, index);
      } else if (isInOrNotInOperator(filter)) {
        var _filterTagsConfig$fil, _propertyDefinitionMa;
        const shouldShowLanguage = !!((_filterTagsConfig$fil = filterTagsConfig[filter.property]) !== null && _filterTagsConfig$fil !== void 0 && _filterTagsConfig$fil.displayLanguageInFilterTag);
        const referencedObjectType = (_propertyDefinitionMa = propertyDefinitionMap[filter.property]) === null || _propertyDefinitionMa === void 0 ? void 0 : _propertyDefinitionMa.referencedObjectType;
        filterLabel = /*#__PURE__*/_jsxs(_Fragment, {
          children: [filter.operator === NOT_IN && /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: "content-management-listing-lib.filterTagsBar.options.notOperator"
            }, index), "\xA0"]
          }), /*#__PURE__*/_jsx(FormattedList, {
            exclusive: true,
            children: filter.values.map((value, key) => {
              return referencedObjectType && value !== SELF_USER_IDENTIFIER ? /*#__PURE__*/_jsx(FilterTagEnumLabel, {
                id: value,
                type: referencedObjectType,
                shouldShowLanguage: shouldShowLanguage
              }, key) : /*#__PURE__*/_jsx(FilterLabel, {
                value: value,
                options: options
              }, key);
            })
          })]
        });
      } else if (isNotEqualOperator(filter)) {
        filterLabel = /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "content-management-listing-lib.filterTagsBar.options.notOperator"
          }, index), "\xA0", /*#__PURE__*/_jsx(FilterLabel, {
            value: filter.value,
            options: options
          }, index)]
        });
      } else if (isRangeOperator(filter)) {
        filterLabel = /*#__PURE__*/_jsx(FilterTagDateLabel, {
          filter: filter
        }, index);
      }
      const filterTag = /*#__PURE__*/_jsxs(Tag
      // aria-label={getTranslatedScreenReaderTextForSiteTree(
      //   'filterTag',
      //   {
      //     filterType: I18n.text(
      //       `content-management-ui.shared.properties.labels.${filter.property}`
      //     ),
      //   }
      // )}
      , {
        closeable: true,
        inline: true,
        onCloseClick: () => handleRemovePropertyFilter(filter.property),
        children: [!isNotHasPropertyOperator(filter) && !isHasPropertyOperator(filter) && /*#__PURE__*/_jsx(FilterTagDescriptorLabel, {
          property: filter.property
        }), filterLabel]
      }, index);
      return filterTag;
    }, [])
  });
};
const FilterTagsBar = () => {
  const {
    filterGroups
  } = useCrmSearchQuery();
  const searchTokens = useSearchTokens();
  const {
    hasViews
  } = useContentPlatformListingConfiguration();
  const {
    propertyDefinitionMap,
    filterValidationConfig
  } = useContentPlatformPropertiesConfiguration();
  const {
    userInfo
  } = useAuthInfoProvider();
  const availableFilters = useMemo(() => filterGroups.length > 0 ? filterGroups[0].filters.filter(filter => getIsPropertyVisibleAsFilter(filter.property, propertyDefinitionMap) && filterValidationConfig.isFilterAvailableForUser(filter, userInfo)) : [], [filterGroups, propertyDefinitionMap, filterValidationConfig, userInfo]);
  if (availableFilters && availableFilters.length === 0 && searchTokens.length === 0) {
    return null;
  }
  return /*#__PURE__*/_jsxs(Flex, {
    "data-test-id": "filter-tags-bar",
    paddingBlock: "200",
    paddingInline: "500",
    minBlockSize: "50px",
    flexWrap: "wrap",
    borderInlineColor: "border-tertiary-default",
    borderInlineWidth: "100",
    borderInlineStyle: hasViews ? 'solid' : 'none',
    children: [/*#__PURE__*/_jsx(Box, {
      alignSelf: "center",
      shrink: 0,
      children: /*#__PURE__*/_jsx(Text, {
        as: "span",
        marginInlineEnd: "200",
        typography: "heading-50",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "content-management-listing-lib.filterTagsBar.title"
        })
      })
    }), /*#__PURE__*/_jsx(Box, {
      alignSelf: "center",
      grow: 1,
      children: /*#__PURE__*/_jsxs(TFlex, {
        align: "center",
        wrap: "wrap",
        children: [/*#__PURE__*/_jsx(SearchTag, {}), /*#__PURE__*/_jsx(FilterTags, {
          availableFilters: availableFilters
        })]
      })
    })]
  });
};
export default FilterTagsBar;